import { useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { fetchWithAuth } from '../lib/auth'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'

interface ImportResult {
  imported: number
  skipped: number
  errors: string[]
}

type Stage = 'info' | 'importing' | 'result' | 'error'

interface Props {
  children: React.ReactNode
  onSuccess: () => void
}

const TEMPLATE_CSV =
  'Date,Name,Amount,Category,Merchant,Notes\n' +
  '2026-01-15,Coffee,5.50,FOOD,Starbucks,\n' +
  '2026-01-16,Groceries,84.20,GROCERIES,Whole Foods,\n'

const VALID_CATEGORIES = [
  'GROCERIES', 'FOOD', 'TRANSPORTATION', 'APPAREL', 'RETAIL',
  'ENTERTAINMENT', 'HEALTH_AND_FITNESS', 'HOUSING', 'UTILITIES', 'TRAVEL', 'TAX', 'OTHER',
]

export function ImportCsvDialog({ children, onSuccess }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [stage, setStage] = useState<Stage | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const openInfo = () => setStage('info')

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setStage('importing')
    setError(null)
    setResult(null)

    try {
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8080'
      const formData = new FormData()
      formData.append('file', file)
      const response = await fetchWithAuth(`${apiBase}/import/csv`, {
        method: 'POST',
        body: formData,
      })
      if (!response.ok) {
        const msg = await response.text()
        throw new Error(msg.trim() || 'Import failed')
      }
      const data: ImportResult = await response.json()
      setResult(data)
      setStage('result')
      if (data.imported > 0) onSuccess()
    } catch (err) {
      Sentry.captureException(err)
      setError(err instanceof Error ? err.message : 'Import failed')
      setStage('error')
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'buylog-import-template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const reset = () => {
    setStage(null)
    setResult(null)
    setError(null)
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleFileChange}
      />

      <span onClick={openInfo}>{children}</span>

      <Dialog open={!!stage} onOpenChange={(open) => !open && reset()}>
        <DialogContent>
          {stage === 'info' && (
            <>
              <DialogHeader>
                <DialogTitle>Import CSV</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <p className="text-muted-foreground">
                  Your CSV must have a header row with at least these columns:
                </p>
                <div className="rounded-md bg-muted px-4 py-3 font-mono text-xs leading-relaxed">
                  <span className="font-semibold">Date</span> · <span className="font-semibold">Name</span> · <span className="font-semibold">Amount</span>
                  <span className="text-muted-foreground"> · Category · Merchant · Notes</span>
                </div>
                <ul className="space-y-1 text-muted-foreground list-disc pl-4">
                  <li><span className="text-foreground font-medium">Date</span> — YYYY-MM-DD format (e.g. 2026-04-14)</li>
                  <li><span className="text-foreground font-medium">Amount</span> — decimal number (e.g. 12.50)</li>
                  <li><span className="text-foreground font-medium">Category</span> — one of: {VALID_CATEGORIES.join(', ')}</li>
                </ul>
                <p className="text-muted-foreground text-xs">
                  If you exported from Buylog, the file is already in the right format.
                </p>
              </div>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" onClick={downloadTemplate} className="flex-1">
                  Download template
                </Button>
                <Button onClick={() => fileInputRef.current?.click()} className="flex-1">
                  Choose file
                </Button>
              </div>
            </>
          )}

          {stage === 'importing' && (
            <>
              <DialogHeader>
                <DialogTitle>Importing…</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">Uploading and processing your file.</p>
            </>
          )}

          {stage === 'result' && result && (
            <>
              <DialogHeader>
                <DialogTitle>Import complete</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p>
                  <span className="font-semibold">{result.imported}</span>{' '}
                  {result.imported === 1 ? 'expense' : 'expenses'} imported
                  {result.skipped > 0 && `, ${result.skipped} row${result.skipped === 1 ? '' : 's'} skipped`}.
                </p>
                {(result.errors?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-muted-foreground mb-1">Row errors:</p>
                    <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
                      {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <Button onClick={reset} className="mt-2">Done</Button>
            </>
          )}

          {stage === 'error' && (
            <>
              <DialogHeader>
                <DialogTitle>Import failed</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-destructive">{error}</p>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" onClick={() => setStage('info')} className="flex-1">Back</Button>
                <Button onClick={reset} className="flex-1">Done</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
